import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnInit,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import { AutoFillDefinition, Field } from '../../Models';
import { BasicField } from '../../Models/ModuleDefinitions/basic-field';
import {
	IAutoFillFieldMapping,
	IAutoFillPropertyMapping,
} from '../../Models/ModuleDefinitions/auto-fill-field-mapping';
import { SourceType } from '../../Models/configuration-options/source-type';
import { ProcessType } from '../../Models/configuration-options/process-type';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'autofill-field-mapping',
    templateUrl: 'autofill-field-mapping.component.html',
    styleUrls: ['autofill-field-mapping.component.less'],
    imports: [MatCard, MatCardTitle, MatCardContent, FlexModule, MatSelect, FormsModule, MatOption, MatIconButton, MatIcon, MatCardActions, MatButton]
})
export class AutofillFieldMappingComponent implements OnInit, OnChanges {
	@Input() autoFillDefinition!: AutoFillDefinition;
	@Input() stepFields: Field[];
	@Input() potentialMappingFields: BasicField[];

	@Output() mappingsChange = new EventEmitter<IAutoFillFieldMapping[]>();

	currentMappings: IAutoFillFieldMapping[] = [];

	ngOnInit(): void {
		this.loadCurrentMappings();
		this.mappingsChange.emit(this.currentMappings);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.autoFillDefinition && !changes.autoFillDefinition.isFirstChange()) {
			this.loadCurrentMappings();
		}
	}

	stepFieldChanged(autoFillFieldMapping: IAutoFillFieldMapping): void {
		const newField = this.stepFields.filter((x) => x.FieldId === autoFillFieldMapping.field.FieldId)[0];
		autoFillFieldMapping.propertyMappings =
			this.getFieldPropertiesForField(newField).map(
				(fieldProperty) => (this.createBlankAutoFillPropertyMapping(fieldProperty))
			);
		this.mappingsChange.emit(this.currentMappings);
	}

	mappingFieldChanged(): void {
		this.mappingsChange.emit(this.currentMappings);
	}

	validPropertyMappings(propertyMappings: IAutoFillPropertyMapping[]): IAutoFillPropertyMapping[] {
		return propertyMappings.filter(
			(pm) =>
				pm.fieldProperty !== 'SelectedItemAsString' &&
				pm.fieldProperty !== 'Time1' &&
				pm.fieldProperty !== 'DateTimeUtc1' &&
				pm.fieldProperty !== 'TimeZoneOffset1' &&
				pm.fieldProperty !== 'Date2' &&
				pm.fieldProperty !== 'Time2' &&
				pm.fieldProperty !== 'DateTimeUtc2' &&
				pm.fieldProperty !== 'TimeZoneOffset2'
		);
	}

	removeMapping(index: number): void {
		this.currentMappings.splice(index, 1);
		this.mappingsChange.emit(this.currentMappings);
	}

	addNewMapping(): void {
		this.currentMappings.push({field: null, propertyMappings: []});
	}

	private loadCurrentMappings(): void {
		this.currentMappings = [];
		if (this.autoFillDefinition) {
			const isServerSideLinkedRecord = this.autoFillDefinition.ProcessType === ProcessType.server &&
			this.sourceType() === SourceType.LinkedRecord;

			for (const fieldId of Object.keys(this.autoFillDefinition?.ControlValueMappings)) {
				const controlValueMapping = this.autoFillDefinition.ControlValueMappings[fieldId];
				const propertyMappings = [];

				for (const fieldProperty of Object.keys(controlValueMapping)) {
					const lookupFieldName = controlValueMapping[fieldProperty];
					const field = isServerSideLinkedRecord ?
						this.getFieldFromMappings(lookupFieldName) :
					  	this.getFieldFromLookupFieldName(lookupFieldName, fieldProperty);

					propertyMappings.push({
						fieldProperty,
						lookupField: field,
					});
				}

				const field = this.stepFields.filter((otherField) => otherField.FieldId === fieldId)[0];
				this.enforceFieldPropertyMappingsForFieldType(field, propertyMappings);

				this.currentMappings.push({
					field,
					propertyMappings,
				});
			}
		}
	}

	private getFieldFromLookupFieldName(lookupFieldName: string, fieldProperty: string) {
		if (this.isDateFieldForFeedLookup(fieldProperty)) {
			lookupFieldName = lookupFieldName.substr(0, lookupFieldName.length - 4);
		}

		if (fieldProperty === 'SelectedItemAsGuid' && lookupFieldName.endsWith('Id')) {
			lookupFieldName = lookupFieldName.substr(0, lookupFieldName.length - 2);
		}

		return this.potentialMappingFields?.filter((lookupField) => lookupField.name === lookupFieldName)[0];
	}

	private getFieldFromMappings(lookupFieldName: string) {
		return this.potentialMappingFields
			?.filter((lookupField) => lookupField.mappings
				.some((mapping) => mapping.modelPropertyName === lookupFieldName))[0];
	}

	private enforceFieldPropertyMappingsForFieldType(field: Field, propertyMappings: any[]) {
		const expectedFieldPropertiesFieldType = this.getFieldPropertiesForField(field);
		for (const fieldProperty of expectedFieldPropertiesFieldType) {
			if (!propertyMappings.find((mapping) => mapping.fieldProperty === fieldProperty)) {
				propertyMappings.push(this.createBlankAutoFillPropertyMapping(fieldProperty));
			}
		}
	}

	private createBlankAutoFillPropertyMapping(fieldProperty: string): IAutoFillPropertyMapping {
		return {
			fieldProperty,
			lookupField: null,
		};
	}

	private isDateFieldForFeedLookup(fieldProperty: string) {
		return fieldProperty === 'Date1' && this.sourceType() === SourceType.Feed;
	}

	private getFieldPropertiesForField(field: Field): string[] {
		const fieldType = this.ParseFieldType(field);
		switch (fieldType) {
			case 'list':
				return ['SelectedItemAsString', 'SelectedText'];

			case 'hierarchylist':
			case 'userlist':
				return ['SelectedItemAsGuid'];

			case 'textbox':
				return ['TextValue'];

			case 'date':
			case 'datetime':
			case 'datetimepicker':
				return ['Time1', 'Date1', 'DateTimeUtc1', 'TimeZoneOffset1','Time2', 'Date2', 'DateTimeUtc2', 'TimeZoneOffset2'];
			case 'checkbox':
				return ['TextValue'];

			case 'geolocation':
				return ['LatValue', 'LongValue'];

			case 'cron':
				return [
					'Schedule',
					'Interval',
					'Intervals',
					'Recurrence',
					'RangeFrom',
					'RangeTo',
				];

			default:
				return ['TextValue'];
		}
	}

	private ParseFieldType(newField: Field) {
		const controlUniqueId = newField.FieldUniqueId;
		if (controlUniqueId === 'list') {
			const listValueType = newField.ListParams.ListType;
			if (listValueType === 'HIERARCHY') {
				return 'hierarchylist';
			}
			else if (listValueType === 'USER') {
				return 'userlist';
			}
		}
		return controlUniqueId;
	}

	private sourceType(): SourceType {
		if (this.autoFillDefinition.LookupTableName) {
            return SourceType.Feed;
        }
        else if (this.autoFillDefinition.QueryModuleId && this.autoFillDefinition.QueryId) {
            return SourceType.Udq;
        }
		return SourceType.LinkedRecord;
    }
}
